import { Head, Link, router } from '@inertiajs/react'
import { withWorkspaceLayout } from '@/app/layouts/WorkspaceLayout'
import { type PageNode } from '@/widgets/sidebar/PageTree'

interface Member {
  id: number
  user_id: number
  user_name: string
  user_email: string
  role: string
  created_at: string
}

interface MembersProps {
  workspace: { slug: string; name: string }
  members: Member[]
  current_user_id: number
  sidebar_pages: PageNode[]
}

const ROLES = ['admin', 'editor', 'viewer']

function Members({ workspace, members, current_user_id }: MembersProps) {
  function handleRoleChange(member: Member, newRole: string) {
    router.patch(`/${workspace.slug}/memberships/${member.id}`, {
      role: newRole,
    }, { preserveScroll: true })
  }

  function handleRemove(member: Member) {
    if (!confirm(`Remove ${member.user_name} from this workspace?`)) return

    router.delete(`/${workspace.slug}/memberships/${member.id}`, {
      preserveScroll: true,
    })
  }

  return (
    <>
      <Head title={`Team - ${workspace.name}`} />
      <div className="mx-auto max-w-2xl px-6 py-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Team</h2>
            <p className="mt-1 text-sm text-gray-500">
              Manage members of {workspace.name}.
            </p>
          </div>
          <Link
            href={`/${workspace.slug}/invitations/new`}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Invite Members
          </Link>
        </div>

        <table className="mt-6 w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Name</th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Email</th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Role</th>
              <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {members.map((member) => {
              const isCurrentUser = member.user_id === current_user_id
              return (
                <tr key={member.id}>
                  <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-900">
                    {member.user_name}
                    {isCurrentUser && (
                      <span className="ml-1 text-xs text-gray-400">(you)</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                    {member.user_email}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-sm">
                    <select
                      value={member.role}
                      onChange={(e) => handleRoleChange(member, e.target.value)}
                      className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      {ROLES.map((role) => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm">
                    {!isCurrentUser && (
                      <button
                        onClick={() => handleRemove(member)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

Members.layout = withWorkspaceLayout
export default Members
